"use client";
import Image from '@/components/ui/MediaImage';
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";

interface TextCenterCtaProps {
    leftImage?: { mediaId?: number; url?: string; alt?: string };
    rightImage?: { mediaId?: number; url?: string; alt?: string };
    title?: string;
    description?: string;
    __grapesPreview?: boolean;
}

export default function TextCenterCta({ 
    leftImage,
    rightImage,
    title = 'Explore Every Corner Of The World With Us.',
    description = 'Join us on an extraordinary journey as we explore every corner of the world together. Our adventures will take you to hidden gems, iconic landmarks, and breathtaking landscapes, offering unforgettable experiences and memories. Discover the beauty and diversity of our planet with us.and breathtaking landscapes, offering unforgettable experiences and memories. Discover the beauty and diversity of our planet with us.',
    __grapesPreview = false,
    ...restProps
}: TextCenterCtaProps) {
    const [componentProps, setComponentProps] = useState<TextCenterCtaProps>({});
    // Unique ID for SVG pattern (her component instance için farklı olmalı)
    // useId() kullanarak hydration hatasını önle (server ve client'ta aynı ID üretir)
    const uniqueId = React.useId();
    const patternId = `pattern-${uniqueId}`;
    const clipPathId = `clip-path-${uniqueId}`;

    // Component hydration: data-component-props'tan props'ları al
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            const element = document.querySelector('[data-component-path="sections/TextCenterCta"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
                        setComponentProps(props);
                    } catch {
                        // Props parse hatası
                    }
                }
            }
        };

        updateProps();

        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/TextCenterCta"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // Props'tan veya componentProps'tan değerleri al
    const finalLeftImage = componentProps.leftImage || leftImage;
    const finalRightImage = componentProps.rightImage || rightImage;
    const finalTitle = componentProps.title || title;
    const finalDescription = componentProps.description || description;
    
    // URL'leri transform et (medya proxy kullanımı için)
    const leftImageUrl = finalLeftImage?.url ? transformMediaUrl(finalLeftImage.url, null) : null;
    const rightImageUrl = finalRightImage?.url ? transformMediaUrl(finalRightImage.url, null) : null;
    
    // Backend render modunu kontrol et
    const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
    
    // Backend render attribute'larını oluştur (sadece backend'de render edilirken eklenir)
    const backendAttributes = getBackendRenderAttributes(
        'sections/TextCenterCta',
        { leftImage, rightImage, title: finalTitle, description: finalDescription },
        isBackendRender
    );
    
    return (
        <section {...backendAttributes} className="flex flex-row gap-5 items-center my-[190px]">
            {leftImageUrl && (
                <motion.div
                    className="flex flex-col"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                        type: "spring" as const,
                        stiffness: 80,
                        damping: 20,
                        delay: 0.2
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        whileHover={{ rotate: -2 }}
                        className="rounded-lg overflow-hidden"
                    >
                        <Image
                            src={leftImageUrl}
                            alt={finalLeftImage?.alt || "left-img"}
                            width={265}
                            height={410}
                            className="h-[410px] object-cover rounded-lg"
                            unoptimized={leftImageUrl.startsWith('/api/media/proxy/')}
                        />
                    </motion.div>
                </motion.div>
            )}
            <motion.div 
                className="flex-1 flex flex-col justify-between gap-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                    type: "spring" as const, 
                    stiffness: 80, 
                    damping: 20,
                    delay: 0.3
                }}
            >
                <motion.h2 
                    className="font-bold text-alsero-second text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight sm:leading-snug"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    data-prop-path="title"
                >
                    {finalTitle}
                </motion.h2>
                <motion.p 
                    className="text-alsero-light-gray text-base sm:text-lg md:text-xl leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    data-prop-path="description"
                >
                    {finalDescription}
                </motion.p>
            </motion.div>
            {rightImageUrl && (
                <motion.div
                    initial={{ opacity: 0, x: 50, scale: 0.95 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                        type: "spring" as const,
                        stiffness: 80,
                        damping: 20,
                        delay: 0.4
                    }}
                    whileHover={{ rotate: 2 }}
                >
                    <div className="flex-1">
                        <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="400" height="510" viewBox="0 0 400 510">
                            <defs>
                                <clipPath id={clipPathId}>
                                    <path d="M111.266,510a10,10,0,0,1-10-10V410H10A10,10,0,0,1,0,400V10A10,10,0,0,1,10,0H288.734a10,10,0,0,1,10,10v90H390a10,10,0,0,1,10,10V500a10,10,0,0,1-10,10Z" transform="translate(210 273)" fill="#fff"/>
                                </clipPath>
                                <pattern id={patternId} preserveAspectRatio="none" width="100%" height="100%" viewBox="0 0 4096 2304">
                                    <image width="4096" height="2304" xlinkHref={rightImageUrl}></image>
                                </pattern>
                            </defs>
                            <g transform="translate(-210 -273)" clipPath={`url(#${clipPathId})`}>
                                <rect width="913.417" height="510" transform="translate(-31.013 273)" fill={`url(#${patternId})`}/>
                            </g>
                        </svg>
                    </div>
                </motion.div>
            )}
        </section>
    )
}